import Konva from 'konva';
import VueKonva from 'vue-konva';
import debounce from 'lodash/debounce';
import { Nullable } from '~/types/common';
import { components } from '~~/api-types';

export type LogoDto = components['schemas']['CreateContentSocialLogoDto'];
export type AddImagePayload = {
  fileUrl: string;
  id: string | number;
  logo?: LogoDto;
  preview?: boolean;
};

export type Box = {
  x: number;
  y: number;
  height: number;
  width: number;
  rad?: number;
  id?: number;
  page?: number;
};

const MIN_QUADRANT_SIZE = 40;

const QUADRANT_DEFAULT_CONFIG = {
  fill: 'rgba(4, 41, 87, 1)',
  strokeScaleEnabled: false,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
};

export const useQuadrants = ({
  emit,
  quadrants: initialValue,
  originalDimensions,
  stage,
  layer,
  transformer,
  imageLayer,
}) => {
  const components: Record<string, any> = {};
  VueKonva.install({
    component(key: string, component: any) {
      components[key] = component;
    },
  });
  const {
    vStage: VStage,
    vLayer: VLayer,
    vRect: VRect,
    vGroup: VGroup,
    vTransformer: VTransformer,
    vImage: VImage,
  } = components;

  const quadrants = ref<Konva.RectConfig[]>([]);
  const scale = ref(1);
  const currentQuadrant = ref('');
  const isSelected = ref(false);
  const logoStatesPerQuadrant = ref<
    Map<string, { image: Konva.Image; state: LogoDto }>
  >(new Map());
  const logoStatesPerPage = ref<Map<number, Map<string, LogoDto>>>(new Map());
  const currentPage = ref<number>(1);
  const currentLogoUrl = ref<string | null>(null);

  const loadQuadrantsFromProps = (quadrantsData: Box[] = []) => {
    if (!stage.value) {
      return;
    }

    const stageEl = stage.value.getStage();
    const stageWidth = stageEl.width();
    const stageHeight = stageEl.height();

    if (stageWidth <= 1 || stageHeight <= 1) {
      return;
    }

    const isPerPageFormat =
      originalDimensions &&
      typeof originalDimensions === 'object' &&
      !('width' in originalDimensions);

    const processedQuadrants = quadrantsData.map((quadrant, index) => {
      const quadrantPage = quadrant.page || 1;

      let refWidth: number;
      let refHeight: number;
      let dimensionSource: string;

      if (isPerPageFormat && (originalDimensions as any)[quadrantPage]) {
        const pageDims = (originalDimensions as any)[quadrantPage];
        refWidth = pageDims.width;
        refHeight = pageDims.height;
        dimensionSource = `per-page (page ${quadrantPage})`;
      } else if (originalDimensions && 'width' in originalDimensions) {
        refWidth = originalDimensions.width;
        refHeight = originalDimensions.height;
        dimensionSource = 'single original dimensions';
      } else {
        refWidth = stageWidth / scale.value;
        refHeight = stageHeight / scale.value;
        dimensionSource = 'calculated from stage';
      }

      const x = quadrant.x * refWidth;
      const y = quadrant.y * refHeight;
      const width = quadrant.width * refWidth;
      const height = quadrant.height * refHeight;

      const processed = {
        ...QUADRANT_DEFAULT_CONFIG,
        x,
        y,
        width,
        height,
        fill: 'rgba(4, 41, 87, 0.5)',
        id: quadrant.id,
        name: `quadrant-${quadrant.id || Math.random()}`,
        page: quadrantPage,
      };

      return processed;
    });

    quadrants.value = processedQuadrants;
  };

  const distance = (x1: number, y1: number, x2: number, y2: number) => {
    return Math.hypot(x2 - x1, y2 - y1);
  };

  const findClosestRectangle = (box: Box): Konva.RectConfig => {
    let [closestRectangle] = quadrants.value;
    let closestDistance = distance(
      box.x + box.width / 2,
      box.y + box.height / 2,
      closestRectangle.x + closestRectangle.width / 2,
      closestRectangle.y + closestRectangle.height / 2,
    );

    for (let i = 1; i < quadrants.value.length; i++) {
      const rect = quadrants.value[i];
      const dist = distance(
        box.x + box.width / 2,
        box.y + box.height / 2,
        rect.x + rect.width / 2,
        rect.y + rect.height / 2,
      );
      if (dist < closestDistance) {
        closestRectangle = rect;
        closestDistance = dist;
      }
    }

    return closestRectangle;
  };

  const moveToQuadrant = (rect, closest) => {
    const newX = Math.max(
      closest.x,
      Math.min(closest.x + closest.width - rect.width, rect.x),
    );
    const newY = Math.max(
      closest.y,
      Math.min(closest.y + closest.height - rect.height, rect.y),
    );

    return { x: newX, y: newY, width: rect.width, height: rect.height };
  };

  const imageRef = ref<Nullable<Konva.Image>>(null);

  const addQuadrant = (coords: Box) => {
    quadrants.value.push({
      ...QUADRANT_DEFAULT_CONFIG,
      x: Number(coords.x),
      y: Number(coords.y),
      height: Number(coords.height),
      width: Number(coords.width),
      name: String(coords.id),
    });
  };

  const updateTransformer = () => {
    const transformerNode = transformer.value?.getNode();

    if (!transformerNode) {
      return;
    }

    if (imageRef.value && isSelected.value) {
      transformerNode.nodes([imageRef.value]);
    } else {
      transformerNode.nodes([]);
    }
  };

  const hideTransformer = () => {
    isSelected.value = false;
    updateTransformer();
  };

  const handleStageMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (e.target === e.target.getStage()) {
      hideTransformer();
      return;
    }

    const clickedOnTransformer =
      e.target.getParent()?.className === 'Transformer';
    if (clickedOnTransformer) {
      return;
    }

    const name = e.target.name();
    isSelected.value = name === 'logo';
    updateTransformer();
  };

  const renderImage = () => {
    if (!stage.value || !layer.value) return;

    const stageEl = stage.value.getStage();
    const layerEl = layer.value.getNode();
    const transformerEl = transformer.value?.getNode();

    if (stageEl.width() === 0 || stageEl.height() === 0) {
      return;
    }

    const allLayers = stageEl.getLayers();
    const imageLayers = stageEl.find('.image-layer');
    const logoImages = stageEl.find('.logo');

    transformerEl?.hide();
    layerEl.hide();

    const scale = stageEl.scaleX();

    const dataUrl = stageEl.toDataURL({
      mimeType: 'image/jpeg',
      pixelRatio: 1 / scale,
    });

    transformerEl?.show();
    layerEl.show();

    isSelected.value = true;
    updateTransformer();
    return dataUrl;
  };

  const emitRenderedImage = debounce(async () => {
    const value = await renderImage();
    const pageIndex = currentPage.value - 1;
    emit('update:imageData', value, pageIndex);
  }, 100);

  const isPointInsideContainers = (point: { x: number; y: number }) => {
    const EPSILON = 0.0001;
    const { x, y } = point;
    return quadrants.value.some((quadrant) => {
      return (
        x >= quadrant.x - EPSILON &&
        x <= quadrant.x + quadrant.width + EPSILON &&
        y >= quadrant.y - EPSILON &&
        y <= quadrant.y + quadrant.height + EPSILON
      );
    });
  };

  const isLogoInsideQuadrants = (logo: Box, range: number): boolean => {
    if (quadrants.value.length === 0) {
      return false;
    }

    for (let { x } = logo; x <= logo.x + logo.width; x += range) {
      if (
        !isPointInsideContainers({ x, y: logo.y }) ||
        !isPointInsideContainers({ x, y: logo.y + logo.height / 2 }) ||
        !isPointInsideContainers({ x, y: logo.y + logo.height })
      ) {
        return false;
      }
    }

    for (let { y } = logo; y <= logo.y + logo.height; y += range) {
      if (
        !isPointInsideContainers({ x: logo.x, y }) ||
        !isPointInsideContainers({ x: logo.x + logo.width / 2, y }) ||
        !isPointInsideContainers({ x: logo.x + logo.width, y })
      ) {
        return false;
      }
    }

    const steps = Math.floor(Math.max(logo.width, logo.height) / range);
    for (let step = 0; step <= steps; step++) {
      const currentStep = step * range;
      const diagonalX1 = logo.x + currentStep;
      const diagonalY1 = logo.y + currentStep;
      const diagonalX2 = logo.x + logo.width - currentStep;
      const diagonalY2 = logo.y + logo.height - currentStep;

      if (
        diagonalX1 <= logo.x + logo.width &&
        diagonalY1 <= logo.y + logo.height &&
        !isPointInsideContainers({ x: diagonalX1, y: diagonalY1 })
      ) {
        return false;
      }
      if (
        diagonalX2 >= logo.x &&
        diagonalY2 >= logo.y &&
        !isPointInsideContainers({ x: diagonalX2, y: diagonalY2 })
      ) {
        return false;
      }
    }

    if (
      !isPointInsideContainers({ x: logo.x, y: logo.y }) ||
      !isPointInsideContainers({ x: logo.x + logo.width, y: logo.y }) ||
      !isPointInsideContainers({ x: logo.x, y: logo.y + logo.height }) ||
      !isPointInsideContainers({
        x: logo.x + logo.width,
        y: logo.y + logo.height,
      })
    ) {
      return false;
    }

    return true;
  };

  const emitLogoPosition = (image: Konva.Image): void => {
    emit('update:logo', {
      x: Math.round(image.attrs.x * 100) / 100,
      y: Math.round(image.attrs.y * 100) / 100,
    });
  };

  const emitLogoDimensions = (width: number, height: number): void => {
    emit('update:logo', {
      width: Math.round(width),
      height: Math.round(height),
    });
  };

  const emitImage = (image: Konva.Image): void => {
    emitLogoPosition(image);
    emitLogoDimensions(image.attrs.width, image.attrs.height);

    emit('update:logo', {
      accountLogoId: image.attrs.id,
    });
  };

  const getFittedImageDimensions = (
    image: Box,
    closestRectangle: Konva.RectConfig,
  ) => {
    if (!closestRectangle.width || !closestRectangle.height) {
      return { width: image.width, height: image.height };
    }

    const imageAspectRatio = image.width / image.height;
    const rectangleAspectRatio =
      closestRectangle.width / closestRectangle.height;

    let fittedWidth: number;
    let fittedHeight: number;

    if (imageAspectRatio > rectangleAspectRatio) {
      fittedWidth = closestRectangle.width;
      fittedHeight = closestRectangle.width / imageAspectRatio;
    } else {
      fittedWidth = closestRectangle.height * imageAspectRatio;
      fittedHeight = closestRectangle.height;
    }

    return { width: fittedWidth, height: fittedHeight };
  };

  const onImageDragOutsideQuadrant = (
    scaledPosition: Box,
    image: Konva.Image,
  ) => {
    const closestRectangle = findClosestRectangle(scaledPosition);

    if (
      !closestRectangle.height ||
      !closestRectangle.width ||
      !closestRectangle.x ||
      !closestRectangle.y
    ) {
      return;
    }

    const newImagePosition = moveToQuadrant(scaledPosition, closestRectangle);
    const isImageSmallerThenRectangle =
      newImagePosition.width <= closestRectangle.width &&
      newImagePosition.height <= closestRectangle.height;

    image.position(newImagePosition);

    if (closestRectangle.name === currentQuadrant.value) {
      currentQuadrant.value = closestRectangle.name || '';
      return;
    }

    if (isImageSmallerThenRectangle) {
      return image;
    }

    const { width, height } = getFittedImageDimensions(
      newImagePosition,
      closestRectangle,
    );

    image.height(height);
    image.width(width);
    image.scaleX(1);
    image.scaleY(1);
    image.position({
      x: closestRectangle.x,
      y: closestRectangle.y,
    });
  };

  const onImageDragMove = (
    event: Konva.KonvaEventObject<MouseEvent>,
    image: Konva.Image,
  ): void => {
    const position = event.target.getClientRect();
    const scaledPosition = {
      x: position.x / scale.value,
      y: position.y / scale.value,
      width: position.width / scale.value,
      height: position.height / scale.value,
    };

    const range = Math.floor(
      Math.min(scaledPosition.width, scaledPosition.height) / 10,
    );

    if (isLogoInsideQuadrants(scaledPosition, range)) {
      emitLogoPosition(image);
      emitRenderedImage();
      return;
    }

    onImageDragOutsideQuadrant(scaledPosition, image);
    emitImage(image);
    emitRenderedImage();
  };

  const saveLogoStateToPage = (
    page: number,
    quadrantId: string,
    state: LogoDto,
  ) => {
    if (!logoStatesPerPage.value.has(page)) {
      logoStatesPerPage.value.set(page, new Map());
    }
    const pageStates = logoStatesPerPage.value.get(page)!;
    pageStates.set(quadrantId, { ...state });
  };

  const getLogoStateForQuadrant = (
    page: number,
    quadrantId: string,
  ): LogoDto | null => {
    const pageStates = logoStatesPerPage.value.get(page);
    if (!pageStates) return null;
    return pageStates.get(quadrantId) || null;
  };

  const updateLogoState = (quadrantId: string, image: Konva.Image) => {
    const existing = logoStatesPerQuadrant.value.get(quadrantId);
    if (existing) {
      existing.state = {
        x: image.x(),
        y: image.y(),
        width: image.width(),
        height: image.height(),
        page: currentPage.value,
        accountLogoId: existing.state.accountLogoId,
      };
      saveLogoStateToPage(currentPage.value, quadrantId, existing.state);
    }
  };

  const saveAllLogosForCurrentPage = () => {
    const page = currentPage.value;

    if (logoStatesPerQuadrant.value.size === 0) {
      return;
    }

    for (const [
      quadrantId,
      { state },
    ] of logoStatesPerQuadrant.value.entries()) {
      saveLogoStateToPage(page, quadrantId, state);
    }
  };

  const addImage = ({ fileUrl, id, logo, preview }: AddImagePayload) => {
    if (!stage.value) {
      return;
    }

    const stageEl = stage.value.getStage();

    if (logoStatesPerQuadrant.value.size > 0) {
      saveAllLogosForCurrentPage();
    }

    const isLogoChanged = currentLogoUrl.value !== fileUrl;
    const detectedPage = quadrants.value[0]?.page || 1;
    const isPageChanged = currentPage.value !== detectedPage;

    if (isLogoChanged || isPageChanged) {
      const oldLayers = stageEl.find('.image-layer');
      for (const layer of oldLayers) {
        layer.destroy();
      }
      hideTransformer();
      logoStatesPerQuadrant.value.clear();
    }

    if (quadrants.value.length === 0) {
      return;
    }

    currentLogoUrl.value = fileUrl;
    currentPage.value = detectedPage;

    if (logo && typeof logo === 'object' && 'x' in logo && 'y' in logo) {
      const logoPage = logo.page || 1;

      const getDimensionsForPage = (page: number) => {
        const defaultDimensions = {
          width: 1080,
          height: 1080,
          type: 'social-file',
        };

        if (!originalDimensions) {
          return defaultDimensions;
        }

        if (
          typeof originalDimensions === 'object' &&
          !('width' in originalDimensions)
        ) {
          const pageDims = (originalDimensions as Record<number, any>)[page];
          return pageDims || defaultDimensions;
        }

        return originalDimensions as {
          width: number;
          height: number;
          type: string;
        };
      };

      const pageDims = getDimensionsForPage(logoPage);
      const refWidth = pageDims.width;
      const refHeight = pageDims.height;

      const logoInPixels = {
        x: (logo.x / 100) * refWidth,
        y: (logo.y / 100) * refHeight,
        width: (logo.width / 100) * refWidth,
        height: (logo.height / 100) * refHeight,
      };

      const matchingQuadrant = quadrants.value.find((q) => {
        const qPage = q.page || 1;
        if (qPage !== logoPage) {
          return false;
        }

        const logoCenterX = logoInPixels.x + logoInPixels.width / 2;
        const logoCenterY = logoInPixels.y + logoInPixels.height / 2;

        const isInside =
          logoCenterX >= q.x &&
          logoCenterX <= q.x + q.width &&
          logoCenterY >= q.y &&
          logoCenterY <= q.y + q.height;

        return isInside;
      });

      if (matchingQuadrant) {
        const quadrantId =
          matchingQuadrant.id?.toString() ||
          matchingQuadrant.name ||
          `q-${quadrants.value.indexOf(matchingQuadrant)}`;
        saveLogoStateToPage(logoPage, quadrantId, {
          x: logoInPixels.x,
          y: logoInPixels.y,
          width: logoInPixels.width,
          height: logoInPixels.height,
          page: logoPage,
          accountLogoId: logo.accountLogoId,
        });
      }
    }
    for (const [quadrantIndex, quadrant] of quadrants.value.entries()) {
      const quadrantId =
        quadrant.id?.toString() || quadrant.name || `q-${quadrantIndex}`;
      const quadrantPage = quadrant.page || 1;

      if (quadrantPage !== currentPage.value) {
        continue;
      }

      Konva.Image.fromURL(fileUrl, (image) => {
        const imgLayer = new Konva.Layer({
          name: 'image-layer',
          id: `logo-layer-${quadrantIndex}`,
        });

        const width = image.width() / scale.value;
        const height = image.height() / scale.value;

        const savedPageState = getLogoStateForQuadrant(
          currentPage.value,
          quadrantId,
        );

        let imagePosition: { x: number; y: number };
        let imageWidth: number;
        let imageHeight: number;

        if (savedPageState) {
          imagePosition = { x: savedPageState.x, y: savedPageState.y };
          imageWidth = savedPageState.width;
          imageHeight = savedPageState.height;
        } else {
          const { width: newWidth, height: newHeight } =
            getFittedImageDimensions(
              { width, height, x: quadrant.x, y: quadrant.y },
              quadrant,
            );

          imagePosition = {
            x: Number(quadrant.x) + (quadrant.width - newWidth) / 2,
            y: Number(quadrant.y) + (quadrant.height - newHeight) / 2,
          };
          imageWidth = newWidth;
          imageHeight = newHeight;
        }

        image.position(imagePosition);
        image.draggable(!preview);
        image.height(imageHeight);
        image.width(imageWidth);
        image.name('logo');
        image.attrs.id = `${id}-quadrant-${quadrantIndex}`;
        image.setAttr('quadrantId', quadrantId);
        image.setAttr('quadrant', quadrant);

        image.on('dragmove', (event) => {
          const img = event.target as Konva.Image;
          const quad = img.getAttr('quadrant');
          const pos = img.position();
          const newX = Math.max(
            quad.x,
            Math.min(quad.x + quad.width - img.width(), pos.x),
          );
          const newY = Math.max(
            quad.y,
            Math.min(quad.y + quad.height - img.height(), pos.y),
          );

          img.position({ x: newX, y: newY });

          const qId = img.getAttr('quadrantId') as string;
          if (qId) {
            updateLogoState(qId, img);
          }

          emitRenderedImage();
        });

        image.on('transform', () => {
          const img = image;
          const quad = img.getAttr('quadrant');

          const newWidth = img.width() * img.scaleX();
          const newHeight = img.height() * img.scaleY();

          const maxWidth = quad.width;
          const maxHeight = quad.height;

          let finalWidth = newWidth;
          let finalHeight = newHeight;

          if (newWidth > maxWidth || newHeight > maxHeight) {
            const scale = Math.min(maxWidth / newWidth, maxHeight / newHeight);
            finalWidth = newWidth * scale;
            finalHeight = newHeight * scale;
          }

          img.width(finalWidth);
          img.height(finalHeight);
          img.scaleX(1);
          img.scaleY(1);

          const pos = img.position();
          const newX = Math.max(
            quad.x,
            Math.min(quad.x + quad.width - finalWidth, pos.x),
          );
          const newY = Math.max(
            quad.y,
            Math.min(quad.y + quad.height - finalHeight, pos.y),
          );
          img.position({ x: newX, y: newY });

          const qId = img.getAttr('quadrantId') as string;
          if (qId) {
            updateLogoState(qId, img);
          }
        });

        image.on('click tap', () => {
          isSelected.value = true;
          imageRef.value = image;
          updateTransformer();
        });

        const logoState = {
          x: imagePosition.x,
          y: imagePosition.y,
          width: imageWidth,
          height: imageHeight,
          page: currentPage.value,
          accountLogoId: typeof id === 'string' ? Number.parseInt(id, 10) : id,
        };

        logoStatesPerQuadrant.value.set(quadrantId, {
          image,
          state: logoState,
        });

        if (quadrantIndex === 0) {
          imageRef.value = image;
        }

        imgLayer.add(image);
        stageEl.add(imgLayer);

        if (quadrantIndex === quadrants.value.length - 1) {
          setTimeout(() => {
            emitRenderedImage();
            if (imageRef.value) {
              emitImage(imageRef.value);
            }
          }, 100);
        }
      });
    }
  };

  const getTransformedImage = (oldValue: Box, newValue: Box) => {
    const scaledNewValue = {
      x: newValue.x / scale.value,
      y: newValue.y / scale.value,
      width: newValue.width / scale.value,
      height: newValue.height / scale.value,
    };

    if (
      scaledNewValue.width < MIN_QUADRANT_SIZE / scale.value ||
      scaledNewValue.height < MIN_QUADRANT_SIZE / scale.value
    ) {
      return oldValue;
    }

    const range = Math.floor(
      Math.min(scaledNewValue.width, scaledNewValue.height) / 10,
    );

    if (isLogoInsideQuadrants(scaledNewValue, range)) {
      return newValue;
    }

    return oldValue;
  };

  const boundFunc = (oldValue: Box, newValue: Box) => {
    const value = getTransformedImage(oldValue, newValue);

    emitLogoDimensions(value.width / scale.value, value.height / scale.value);
    emitRenderedImage();

    return value;
  };

  onMounted(() => {
    if (stage.value) {
      const stageEl = stage.value.getStage();
      scale.value = stageEl.scaleX();
    }
  });

  const rerenderStage = (imgId: string) => {
    const imageEl = document.querySelector(`#${imgId}`) as HTMLImageElement;
    if (!imageEl) {
      return;
    }

    Konva.Image.fromURL(imageEl?.src || '', (imageNode: Konva.Image) => {
      const node = imageLayer.value?.getNode();
      node.children = [];
      node.add(imageNode);
    });

    let width = imageEl?.clientWidth || 0;
    let height = imageEl?.clientHeight || 0;

    const naturalWidth = imageEl?.naturalWidth || 0;
    const naturalHeight = imageEl?.naturalHeight || 0;
    const aspectRatio = naturalWidth / naturalHeight;

    if (!naturalWidth || !naturalHeight || !width || !height) {
      return;
    }

    if (aspectRatio > 1) {
      height = Math.round(width / aspectRatio);
      scale.value = width / naturalWidth;
    } else {
      width = Math.round(height * aspectRatio);
      scale.value = height / naturalHeight;
    }

    const stageEl = stage.value.getStage();

    stageEl.height(height);
    stageEl.width(width);

    stageEl.scale({
      x: scale.value,
      y: scale.value,
    });

    if (imageRef.value) {
      emitRenderedImage();
    }
  };

  const getAllLogos = (accountLogoId?: number | string): LogoDto[] => {
    let numericAccountLogoId: number | undefined;

    if (accountLogoId !== undefined) {
      if (typeof accountLogoId === 'number') {
        numericAccountLogoId = accountLogoId;
      } else if (accountLogoId.includes('-quadrant-')) {
        const [idPart] = accountLogoId.split('-quadrant-');
        numericAccountLogoId = Number.parseInt(idPart, 10);
      } else {
        numericAccountLogoId = Number.parseInt(accountLogoId, 10);
      }

      if (Number.isNaN(numericAccountLogoId)) {
        numericAccountLogoId = undefined;
      }
    }

    saveAllLogosForCurrentPage();

    const getDimensionsForPage = (page: number) => {
      const defaultDimensions = {
        width: 1080,
        height: 1080,
        type: 'social-file',
      };

      if (!originalDimensions) {
        return defaultDimensions;
      }

      if (
        typeof originalDimensions === 'object' &&
        !('width' in originalDimensions)
      ) {
        const pageDims = (originalDimensions as Record<number, any>)[page];
        return pageDims || defaultDimensions;
      }

      return originalDimensions as {
        width: number;
        height: number;
        type: string;
      };
    };

    const logos: LogoDto[] = [];

    for (const [page, pageStates] of logoStatesPerPage.value.entries()) {
      const pageDims = getDimensionsForPage(page);
      const refWidth = pageDims.width;
      const refHeight = pageDims.height;

      for (const [quadrantId, state] of pageStates.entries()) {
        const logoAsPercentage = {
          x: Math.round((state.x / refWidth) * 100 * 100) / 100,
          y: Math.round((state.y / refHeight) * 100 * 100) / 100,
          width: Math.round((state.width / refWidth) * 100 * 100) / 100,
          height: Math.round((state.height / refHeight) * 100 * 100) / 100,
          page,
          accountLogoId: numericAccountLogoId ?? state.accountLogoId,
        };

        logos.push(logoAsPercentage);
      }
    }

    return logos;
  };

  const clearLogos = (saveBeforeClearing = false) => {
    if (saveBeforeClearing) {
      saveAllLogosForCurrentPage();
    }

    const stageEl = stage.value?.getStage();
    if (!stageEl) {
      return;
    }

    const oldLayers = stageEl.find('.image-layer');
    for (const layer of oldLayers) {
      layer.destroy();
    }
    logoStatesPerQuadrant.value.clear();
    imageRef.value = null;
    isSelected.value = false;
  };

  return {
    VRect,
    VStage,
    VLayer,
    VGroup,
    VTransformer,
    VImage,
    quadrants,
    addImage,
    renderImage,
    handleStageMouseDown,
    boundFunc,
    rerenderStage,
    getAllLogos,
    loadQuadrantsFromProps,
    clearLogos,
    saveAllLogosForCurrentPage,
    currentPage,
  };
};
